import { PayloadAction, createSlice } from '@reduxjs/toolkit';
import {
  GetCompaniesParams,
  GetCompaniesResponse,
} from '../interfaces/get-companies';
import { RequestError } from '../../../interfaces/error.interface';
import { createEntityAdapter } from '../../../helpers/create-entity-adapter';
import { Company } from '../../../interfaces/company.interface';

export interface CompanyState {
  companyId: number | null,
  companies: {
    ids: string[];
    entities: Record<string, Company>;
  };
  company: Company | null;
  options: GetCompaniesParams;
  page: number;
  size: number;
  total: number;
  noMore: boolean;
  errorCode: number | null;
}

const initialState: CompanyState = {
  companyId: null,
  companies: {
    ids: [],
    entities: {},
  },
  company: null,
  options: {},
  page: 1,
  size: 100,
  total: 0,
  noMore: false,
  errorCode: null,
};

const companyAdapter = createEntityAdapter<Company>((company: Company) => company.id);

const companySlice = createSlice({
  name: 'company',
  initialState,
  reducers: {
    getCompaniesInitApiAction: (state) => {
      state.errorCode = null;
    },
    getCompaniesSuccessApiAction: (state, action: PayloadAction<GetCompaniesResponse>) => {
      companyAdapter.addMany(action.payload.companies, state.companies);
      state.total = action.payload.pagination.total_elements;
      state.page = action.payload.pagination.page;
      state.size = action.payload.pagination.size;
      state.noMore = action.payload.companies.length < state.size;
    },
    getCompaniesFailureApiAction: (state, action: PayloadAction<RequestError>) => {
      state.errorCode = action.payload.errorCode;
    },
    postCompanyInitApiAction: (state) => {
      state.errorCode = null;
    },
    postCompanySuccessApiAction: () => {},
    postCompanyFailuireApiAction: (state, action: PayloadAction<RequestError>) => {
      state.errorCode = action.payload.errorCode;
    },
    putCompanyInitApiAction: (state) => {
      state.errorCode = null;
    },
    putCompanySuccessApiAction: () => {},
    putCompanyFailuireApiAction: (state, action: PayloadAction<RequestError>) => {
      state.errorCode = action.payload.errorCode;
    },
    deleteCompanyInitApiAction: () => {},
    deleteCompanySuccessApiAction: () => {},
    deleteCompanyFailuireApiAction: () => {},
    setCompanyAction: (state, action: PayloadAction<Company>) => {
      state.company = action.payload;
    },
    setOptionsAction: (state, action: PayloadAction<GetCompaniesParams>) => {
      state.options = action.payload;
      companyAdapter.removeAll(state.companies);
      state.page = 1;
    },
    setCompanyIdAction: (state, action: PayloadAction<number>) => {
      state.companyId = action.payload;
    },
    resetOptionsAction: (state) => {
      state.options = {};
    },
    increasePageAction: (state) => {
      state.page += 1;
    },
    resetPageAction: (state) => {
      state.page = 1;
    },
    companyClearAction: (state) => {
      state.company = null;
      state.errorCode = null;
    },
  },
});

export const {
  getCompaniesInitApiAction,
  getCompaniesSuccessApiAction,
  getCompaniesFailureApiAction,
  postCompanyInitApiAction,
  postCompanySuccessApiAction,
  postCompanyFailuireApiAction,
  putCompanyInitApiAction,
  putCompanySuccessApiAction,
  putCompanyFailuireApiAction,
  deleteCompanyInitApiAction,
  deleteCompanySuccessApiAction,
  deleteCompanyFailuireApiAction,
  setCompanyAction,
  increasePageAction,
  resetPageAction,
  setCompanyIdAction,
  setOptionsAction,
  resetOptionsAction,
  companyClearAction,
} = companySlice.actions;

export default companySlice.reducer;
